<template>
  <form :class="formClasses" :style="formStyles" @submit.prevent="handleSubmit">
    <!-- Before fields slot -->
    <slot name="before-fields" />
    
    <!-- Render fields -->
    <template v-for="field in visibleFields">
      <!-- Custom field slot -->
      <slot :name="`field-${field.name}`" :field="field">
        <themed-form-field
          :key="field.name"
          :label="field.label"
          :error="mergedErrors[field.name]"
          :help-text="field.helpText"
          :tooltip="field.tooltip"
          :tooltip-align="field.tooltipAlign"
          :required="field.required"
          :icon="field.icon"
          :icon-type="field.iconType"
          :info-icon="field.infoIcon"
          :layout="fieldLayout"
          :field-name="field.name"
          :class="fieldGridClasses(field)"
        >
          <template v-slot="{ fieldId, errorId, helpId, hasError, required }">
            <component
              :is="getFieldComponent(field)"
              :id="fieldId"
              :value="modelValue[field.name]"
              v-bind="getFieldProps(field)"
              :error="Boolean(mergedErrors[field.name])"
              :disabled="loading || (field.componentProps && field.componentProps.disabled)"
              :aria-invalid="hasError ? 'true' : null"
              :aria-describedby="getAriaDescribedBy(hasError, errorId, helpId, field.helpText)"
              :aria-required="required ? 'true' : null"
              @input="handleFieldInput(field.name, $event, field)"
            />
          </template>
        </themed-form-field>
      </slot>
    </template>
    
    <!-- After fields slot -->
    <slot name="after-fields" />
    
    <!-- Submit button slot -->
    <slot name="submit" :loading="loading" />
  </form>
</template>

<script>
import ThemedFormField from './ThemedFormField.vue';
import ThemedInput from '../ThemedInput.vue';
import ThemedSelect from '../ThemedSelect.vue';
import ThemedCheckbox from '../ThemedCheckbox.vue';
import ThemedCheckboxGroup from '../ThemedCheckboxGroup.vue';
import ThemedRadio from '../ThemedRadio.vue';
import ThemedRadioGroup from '../ThemedRadioGroup.vue';
import ThemedToggle from '../ThemedToggle.vue';
import ThemedSlider from '../ThemedSlider.vue';
import ThemedMultiSelect from '../ThemedMultiSelect.vue';

/**
 * ThemedForm - Configuration-based form renderer
 * 
 * A powerful form component that renders forms from configuration objects,
 * supporting multiple layouts, conditional fields, automatic validation, and error handling.
 * 
 * Features:
 * - Configuration-based field rendering
 * - Built-in validation with on-change support
 * - Multiple layout options (vertical, horizontal, grid)
 * - Conditional field visibility
 * - Automatic error handling and display
 * - Accessible form controls
 * 
 * Validation:
 * Add a `validation` object to any field configuration to enable automatic validation:
 * - required: Boolean - Field is required
 * - email: Boolean - Validate email format
 * - pattern: RegExp - Custom regex pattern
 * - minLength: Number - Minimum character length
 * - maxLength: Number - Maximum character length
 * - matchField: String - Must match another field (e.g., password confirmation)
 * - message: String - Custom error message
 * - customFn: Function - Custom validation function (value, formData) => true | errorMessage
 * 
 * @example
 * <themed-form
 *   :fields="formFields"
 *   :model-value="formData"
 *   :errors="errors"
 *   layout="grid"
 *   :columns="12"
 *   :validate-on-change="true"
 *   @input="handleInput"
 *   @submit="handleSubmit"
 * >
 *   <template #submit="{ loading }">
 *     <button type="submit" :disabled="loading">Submit</button>
 *   </template>
 * </themed-form>
 * 
 * @example Field with validation
 * {
 *   name: 'email',
 *   type: 'input',
 *   inputType: 'email',
 *   label: 'Email',
 *   required: true,
 *   validation: {
 *     required: true,
 *     email: true
 *   }
 * }
 * 
 * @see ThemedFormExample.vue for complete usage examples
 */
export default {
  name: 'ThemedForm',

  // Vue 2: map v-model to modelValue prop (Vue 3 naming convention)
  model: {
    prop: 'modelValue',
    event: 'update:modelValue'
  },

  components: {
    ThemedFormField,
    ThemedInput,
    ThemedSelect,
    ThemedCheckbox,
    ThemedCheckboxGroup,
    ThemedRadio,
    ThemedRadioGroup,
    ThemedToggle,
    ThemedSlider,
    ThemedMultiSelect
  },
  
  props: {
    fields: {
      type: Array,
      required: true,
      validator: (fields) => {
        // Validate that all fields have required properties
        return fields.every(field => field.name && field.type);
      }
    },
    modelValue: {
      type: Object,
      required: true
    },
    errors: {
      type: Object,
      default: () => ({})
    },
    layout: {
      type: String,
      default: 'vertical',
      validator: (v) => ['vertical', 'horizontal', 'grid'].includes(v)
    },
    columns: {
      type: Number,
      default: 2,
      validator: (v) => v >= 1 && v <= 12
    },
    loading: {
      type: Boolean,
      default: false
    },
    validateOnChange: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      internalErrors: {}
    };
  },
  computed: {
    visibleFields() {
      return this.fields.filter(field => this.isFieldVisible(field));
    },
    
    formClasses() {
      return [
        'themed-form',
        `themed-form--${this.layout}`,
        { 'themed-form--loading': this.loading }
      ];
    },
    
    formStyles() {
      if (this.layout === 'grid') {
        return {
          '--form-columns': this.columns
        };
      }
      return {};
    },
    
    fieldLayout() {
      // Grid layout uses vertical layout for individual fields
      return this.layout === 'grid' ? 'vertical' : this.layout;
    },
    
    // Merge external errors with internal validation errors
    mergedErrors() {
      return { ...this.internalErrors, ...this.errors };
    }
  },
  methods: {
    getFieldComponent(field) {
      const componentMap = {
        input: 'themed-input',
        select: 'themed-select',
        textarea: 'themed-textarea',
        checkbox: 'themed-checkbox',
        'checkbox-group': 'themed-checkbox-group',
        radio: 'themed-radio-group',
        toggle: 'themed-toggle',
        slider: 'themed-slider',
        date: 'themed-date-picker',
        multiselect: 'themed-multi-select',
        autocomplete: 'themed-autocomplete',
        currency: 'themed-currency-input',
        number: 'themed-number-input',
        percent: 'themed-percent-input'
      };
      
      const component = componentMap[field.type];
      
      if (!component) {
        console.warn(`Unknown field type: ${field.type}`);
      } else {
        console.log(`Rendering field ${field.name} with component: ${component}`);
      }
      
      return component || 'themed-input';
    },
    
    getFieldProps(field) {
      const baseProps = {
        placeholder: field.placeholder,
        ...field.componentProps
      };
      
      // Add type-specific props
      if (field.type === 'input') {
        baseProps.type = field.inputType || 'text';
      }
      
      if (field.type === 'select' || field.type === 'multiselect' || field.type === 'radio' || field.type === 'checkbox-group') {
        // Normalize options for ThemedSelect, ThemedRadioGroup, and ThemedCheckboxGroup
        let options = field.options || [];
        
        // If componentProps has option-label and option-value, transform the options
        if (field.componentProps && (field.componentProps['option-label'] || field.componentProps['option-value'])) {
          const labelKey = field.componentProps['option-label'] || 'label';
          const valueKey = field.componentProps['option-value'] || 'value';
          
          options = options.map(opt => {
            if (typeof opt === 'object') {
              return {
                label: opt[labelKey],
                value: opt[valueKey],
                disabled: opt.disabled || false,
                ...opt // Keep original data for slots
              };
            }
            return opt;
          });
          
          // Remove option-label and option-value from baseProps as they're not needed
          delete baseProps['option-label'];
          delete baseProps['option-value'];
        }
        
        baseProps.options = options;
        
        // Add name prop for radio groups (required by ThemedRadioGroup)
        if (field.type === 'radio') {
          baseProps.name = field.name;
        }
        
        // Add checkbox-group specific props
        if (field.type === 'checkbox-group') {
          baseProps.showSelectAll = field.showSelectAll !== false;
          baseProps.selectAllLabel = field.selectAllLabel || 'Select All';
          baseProps.trackBy = field.trackBy || 'value';
        }
      }
      
      if (field.type === 'textarea') {
        baseProps.rows = field.rows;
        baseProps.richText = field.richText;
        baseProps.editorOptions = field.editorOptions;
      }
      
      if (field.type === 'date') {
        baseProps.type = field.dateType || 'date';
        baseProps.format = field.format;
        baseProps.minDate = field.minDate;
        baseProps.maxDate = field.maxDate;
      }
      
      if (field.type === 'multiselect') {
        baseProps.searchable = field.searchable !== false;
        baseProps.grouped = field.grouped;
        baseProps.closeOnSelect = field.closeOnSelect;
      }
      
      if (field.type === 'autocomplete') {
        baseProps.filterFn = field.filterFn;
        baseProps.asyncFn = field.asyncFn;
      }
      
      if (['currency', 'number', 'percent'].includes(field.type)) {
        baseProps.isDecimal = field.isDecimal;
      }
      
      if (field.type === 'slider') {
        baseProps.min = field.componentProps?.min || 0;
        baseProps.max = field.componentProps?.max || 100;
        baseProps.step = field.componentProps?.step || 1;
        baseProps.showValue = field.componentProps?.showValue !== false;
      }
      
      return baseProps;
    },
    
    isFieldVisible(field) {
      // No conditional - always visible
      if (!field.conditional && !field.conditionalFn) {
        return true;
      }
      
      // Function-based conditional
      if (field.conditionalFn && typeof field.conditionalFn === 'function') {
        try {
          return field.conditionalFn(this.modelValue);
        } catch (e) {
          if (import.meta.env.DEV) {
            console.warn(`Error evaluating conditionalFn for field ${field.name}:`, e);
          }
          return true;
        }
      }
      
      // Simple conditional
      if (field.conditional) {
        try {
          const { field: watchField, value } = field.conditional;
          return this.modelValue[watchField] === value;
        } catch (e) {
          if (import.meta.env.DEV) {
            console.warn(`Invalid conditional for field ${field.name}:`, e);
          }
          return true;
        }
      }
      
      return true;
    },
    
    fieldGridClasses(field) {
      if (this.layout !== 'grid') {
        return '';
      }
      
      const col = field.col || 1;
      return `themed-form__field--col-${col}`;
    },
    
    handleFieldInput(fieldName, value, field) {
      // Create a new object to avoid direct mutation
      const updatedData = { ...this.modelValue, [fieldName]: value };
      
      // Validate on change if enabled and field has validation config
      if (this.validateOnChange && field && field.validation) {
        this.validateField(field, value, updatedData);
      }
      
      this.$emit('update:modelValue', updatedData);
      this.$emit('input', updatedData); // keep for backward compat
    },
    
    validateField(field, value, formData = null) {
      const validation = field.validation;
      const data = formData || this.modelValue;
      
      if (!validation) return true;
      
      // Clear existing error for this field
      if (this.internalErrors[field.name]) {
        this.$delete(this.internalErrors, field.name);
      }
      
      // Required validation
      if (validation.required) {
        if (!value || (typeof value === 'string' && value.trim() === '')) {
          this.$set(this.internalErrors, field.name, ['This field is required.']);
          return false;
        }
      }
      
      // Skip other validations if field is empty and not required
      if (!value || (typeof value === 'string' && value.trim() === '')) {
        return true;
      }
      
      // Email validation
      if (validation.email) {
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailPattern.test(value)) {
          this.$set(this.internalErrors, field.name, ['Please enter a valid email address.']);
          return false;
        }
      }
      
      // Pattern validation
      if (validation.pattern) {
        const pattern = validation.pattern instanceof RegExp ? validation.pattern : new RegExp(validation.pattern);
        if (!pattern.test(typeof value === 'string' ? value.trim() : String(value))) {
          this.$set(this.internalErrors, field.name, [validation.message || 'Invalid format.']);
          return false;
        }
      }
      
      // Min length validation
      if (validation.minLength && String(value).length < validation.minLength) {
        this.$set(this.internalErrors, field.name, [validation.message || `Minimum ${validation.minLength} characters required.`]);
        return false;
      }
      
      // Max length validation
      if (validation.maxLength && String(value).length > validation.maxLength) {
        this.$set(this.internalErrors, field.name, [validation.message || `Maximum ${validation.maxLength} characters allowed.`]);
        return false;
      }
      
      // Match field validation (for password confirmation)
      if (validation.matchField) {
        const matchValue = data[validation.matchField];
        if (value !== matchValue) {
          this.$set(this.internalErrors, field.name, [validation.message || 'Fields do not match.']);
          return false;
        }
      }
      
      // Custom validation function
      if (validation.customFn && typeof validation.customFn === 'function') {
        const result = validation.customFn(value, data);
        if (result !== true) {
          this.$set(this.internalErrors, field.name, [typeof result === 'string' ? result : 'Validation failed.']);
          return false;
        }
      }
      
      return true;
    },
    
    validateAllFields() {
      let isValid = true;
      
      this.visibleFields.forEach(field => {
        if (field.validation) {
          const fieldValid = this.validateField(field, this.modelValue[field.name]);
          if (!fieldValid) {
            isValid = false;
          }
        }
      });
      
      return isValid;
    },
    
    clearInternalErrors() {
      this.internalErrors = {};
    },
    
    handleSubmit() {
      // Validate all fields before submit if any field has validation
      const hasValidation = this.visibleFields.some(field => field.validation);
      
      if (hasValidation) {
        const isValid = this.validateAllFields();
        if (!isValid) {
          // Don't submit if validation fails
          return;
        }
      }
      
      this.$emit('submit', this.modelValue);
    },
    
    getAriaDescribedBy(hasError, errorId, helpId, helpText) {
      const ids = [];
      if (hasError) {
        ids.push(errorId);
      } else if (helpText) {
        ids.push(helpId);
      }
      return ids.length > 0 ? ids.join(' ') : null;
    }
  }
}
</script>
